package horcrux.common.utils

import arrow.core.Either
import arrow.core.Option
import arrow.core.raise.Raise
import arrow.core.raise.either
import arrow.core.toOption
import arrow.resilience.Schedule
import arrow.resilience.ScheduleStep
import horcrux.common.HorcruxEither
import horcrux.common.HorcruxRequestSimpleError
import kotlinx.coroutines.currentCoroutineContext
import kotlinx.coroutines.delay
import kotlinx.coroutines.ensureActive
import kotlin.time.Duration.Companion.ZERO

fun <A> HorcruxEither<A>.eitherWay(block: () -> Unit): HorcruxEither<A> = this.mapLeft {
    block()
    it
}.map {
    block()
    it
}

fun <A> Option<A>.ifEmpty(block: () -> Unit): Option<A> {
    if (this.isNone()) {
        block()
    }
    return this
}

interface Validatable<A> {
    fun validate(): Either<Throwable, A>

    fun isInvalid(): Boolean = this.validate().isLeft()
    fun isValid(): Boolean = !isInvalid()
}

suspend fun <A> runIOAndCatch(block: suspend () -> A): HorcruxEither<A> = Either.catch { block() }

class RetryPolicy<Result>(
    private val retrySchedule: Schedule<Throwable, *>, private val shouldRetry: (Throwable) -> Boolean = { true }
) : AppLogger {
    /**
     * Retries action according to schedule until either schedule is exhausted, an error that shouldRetry maps to false is encountered, or action is successful
     */
    suspend fun retry(
        action: suspend Raise<Throwable>.() -> Result,
    ): Either<Throwable, Result> {
        var step: ScheduleStep<Throwable, *> = retrySchedule.step
        while (true) {
            currentCoroutineContext().ensureActive()
            when (val result = either { Either.catch { action.invoke() }.bind() }) {
                is Either.Left -> if (shouldRetry(result.value)) {
                    when (val decision = step(result.value)) {
                        is Schedule.Decision.Continue -> {
                            logger.debug("Received error, retrying.", result.value)
                            if (decision.delay != ZERO) delay(decision.delay)
                            step = decision.step
                        }

                        is Schedule.Decision.Done -> return result
                    }
                } else {
                    return result
                }

                is Either.Right -> return result
            }
        }
    }
}

suspend fun <Error : HorcruxRequestSimpleError, Result> runIOAndCatch(
    mapError: (Throwable) -> Error,
    retryPolicyMaybe: RetryPolicy<Result>? = null,
    block: suspend Raise<Throwable>.() -> Result
): Either<Error, Result> =
    (retryPolicyMaybe?.retry(block) ?: either { Either.catch { block() }.bind() }).mapLeft(mapError)

class MaybeIsEmpty : Throwable()

fun <A> A?.toEither(): HorcruxEither<A> =
    this.toOption().toEither { MaybeIsEmpty() }

fun <A> HorcruxEither<A>.getOrThrow(): A = this.fold({ throw it }, { it })

fun Boolean.toOptional(): Unit? = if (this) {
} else {
    null
}

fun <A> A.ifThen(predicate: Boolean, block: A.() -> A) =
    if (predicate) {
        block()
    } else {
        this
    }
